import {
    ForwardedRef,
    forwardRef,
    ReactElement,
    useImperativeHandle,
    useRef,
} from "react";
import {
    Button,
    Card,
    CardHeader,
    CardContent,
    Grid,
    Typography,
    ThemeProvider,
    Box,
    createTheme,
} from "@mui/material";
import BaseModel from "../models/_BaseModel";
import { ListItemContainerType, ListItemType } from "../utils/Types";
import '../../src/styles/fonts/inter/inter.css';
import '../../src/styles/all.css';
import "../../src/styles/images/busi_daihatsu.jpg";
import { ButtonGridTheme, GridColorStyles } from "../expanded-theme";
import { deepmerge } from "@mui/utils";
import { ExpandMore as MoreVertIcon } from "@mui/icons-material";
import moment from "moment";
import { dataCountPerRowLg, dataCountPerRowMd, dataCountPerRowSm, dataCountPerRowXl, DATE_FORMAT_DISPLAY_DEFAULT, DATETIME_FORMAT_DEFAULT, getDecimalDisplay, getIntegerDisplay } from "../utils/Common";

function ListItemComponent<T extends BaseModel>(props: ListItemType<T>, ref: ForwardedRef<T>) {
    const { data, keyIdx, actionExists, colCountInRow, titleColumnName, details, openMenuButton, onMenuButtonClick, onMenuButtonClose, onMenuItemClick } = props;
    const dataRef = useRef<T>(data);

    useImperativeHandle(ref, () => dataRef.current);

    const handleMenuButtonClick = (event: any, selectedData: T) => {
        onMenuButtonClick(selectedData, event);
    };

    return (
        <Grid
            item
            xl={12 / (colCountInRow ?? dataCountPerRowXl)}
            lg={12 / dataCountPerRowLg}
            md={12 / dataCountPerRowMd}
            sm={12 / dataCountPerRowSm}
            xs={12 / dataCountPerRowSm}
            key={`data_cell_${keyIdx}`}
            sx={{marginBottom: "1rem", display: "flex", justifyContent: "center", alignItems: "center"}}
        >
            <Card sx={{ backgroundColor: "#fefefe", boxShadow: "0px 2px 10px 2px rgba(0, 0, 0, 0.2)", width: "100%", borderRadius: "0.5rem" }}>
                <CardHeader
                    title={(data as any)[titleColumnName ?? ""] ?? "-"}
                    titleTypographyProps={{ fontSize: "0.9rem", fontWeight: "bolder" }}
                    sx={{
                        paddingY: "0.5rem",
                        paddingX: "1rem",
                        alignItems: "center",
                        "& .MuiCardHeader-content": {
                            flexGrow: 2,
                        },
                        '& .MuiCardHeader-action': {
                            display: "flex",
                            flexWrap: "wrap",
                            flexGrow: 1,
                            justifyContent: "end",
                            alignItems: "center",
                            height: "100%",
                            maxWidth: "40%",
                            borderRadius: 0,
                        },
                    }}
                    className="bg-grid-normal text-bg-grid-normal"
                    action={
                        actionExists ?
                        <ThemeProvider theme={createTheme(deepmerge(GridColorStyles, ButtonGridTheme))}>
                            <Button
                                size="small"
                                variant="outlined"
                                color="dark"
                                id="dropdown-button"
                                aria-controls={openMenuButton ? 'basic-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={openMenuButton ? 'true' : undefined}
                                onClick={(e) => handleMenuButtonClick(e, data)}
                            >
                                <MoreVertIcon fontSize="small" sx={{paddingBottom: 0, fontSize: "0.8rem"}} />
                            </Button>
                        </ThemeProvider>
                        :
                        null
                    }
                />
                <CardContent sx={{paddingTop: "0.5rem", paddingBottom: "0.4rem !important", paddingX: "1rem"}}>
                    <Typography variant="body2" color="text.secondary">
                    {
                        details?.map((dt, dt_idx) => {
                            const value = dt.fieldType ?
                                (
                                    dt.fieldType === "boolean" ?
                                    ((data as any)[dt.fieldName] ? "Y" : "T")
                                    :
                                    dt.fieldType === "date" ?
                                    ((data as any)[dt.fieldName] ? moment((data as any)[dt.fieldName]).format(DATE_FORMAT_DISPLAY_DEFAULT) : "N/A")
                                    :
                                    dt.fieldType === "datetime" ?
                                    ((data as any)[dt.fieldName] ? moment((data as any)[dt.fieldName]).format(DATETIME_FORMAT_DEFAULT) : "N/A")
                                    :
                                    dt.fieldType === "integer" ?
                                    ((data as any)[dt.fieldName] ? getIntegerDisplay((data as any)[dt.fieldName] as number) : "N/A")
                                    :
                                    dt.fieldType === "float" ?
                                    ((data as any)[dt.fieldName] ? getDecimalDisplay((data as any)[dt.fieldName] as number) : "N/A")
                                    :
                                    ((data as any)[dt.fieldName] ?? "N/A")
                                )
                                :
                                (data as any)[dt.fieldName] ?? "N/A";

                            return (
                                <Box
                                    sx={{display: "flex", flexDirection: "row", justifyContent: "start", alignItems: "start", width: "100%"}}
                                    key={`data_cell_details_${keyIdx}_${dt_idx}`}
                                >
                                    <Grid item xl={4} lg={4} md={5} sm={6} xs={6}><span style={{whiteSpace: "nowrap", display: "inline-block", overflow: "hidden", width: "100%", fontSize: "0.7rem"}}>{dt.title}</span></Grid>
                                    <Grid item xl={8} lg={8} md={7} sm={6} xs={6}>
                                        <span style={{whiteSpace: "nowrap", display: "inline-block", textOverflow: "ellipsis", overflow: "hidden", width: "100%", fontSize: "0.7rem"}}>
                                            :
                                            <b>{value}</b>
                                        </span>
                                    </Grid>
                                {
                                    dt_idx === details?.length - 1 ? null : <br/>
                                }
                                </Box>
                            );
                        })
                    }
                    </Typography>
                </CardContent>
            </Card>
        </Grid>
    );
};

const ListItem = forwardRef(ListItemComponent) as <T extends BaseModel>(props: ListItemContainerType<T>) => ReactElement;

export default ListItem;
